"use client";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import { validateChileanPhone } from "@calcom/lib/validations/chilean-phone";
import { validateRut } from "@calcom/lib/validations/chilean-rut";
import { TextField } from "@calcom/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

export type BillingProfileFormValues = {
  businessName: string;
  rut: string;
  address: string;
  businessActivity: string;
  contactName: string;
  phone: string;
  email: string;
};

type BillingProfileFormProps = {
  defaultValues?: Partial<BillingProfileFormValues>;
  onSubmit: (data: BillingProfileFormValues) => void | Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
  formId?: string;
  onValidChange?: (isValid: boolean) => void;
};

export const BillingProfileForm = ({
  defaultValues,
  onSubmit,
  isSubmitting = false,
  submitLabel,
  formId = "billing-profile-form",
  onValidChange,
}: BillingProfileFormProps) => {
  const { t } = useLocale();

  const formSchema = useMemo(
    () =>
      z.object({
        businessName: z.string().trim().min(1, t("onboarding_billing_required_field")),
        rut: z
          .string()
          .trim()
          .min(1, t("onboarding_billing_required_field"))
          .refine((value) => validateRut(value), { message: t("onboarding_billing_rut_invalid") }),
        address: z.string().trim().min(1, t("onboarding_billing_required_field")),
        businessActivity: z.string().trim().min(1, t("onboarding_billing_required_field")),
        contactName: z.string().trim().min(1, t("onboarding_billing_required_field")),
        phone: z
          .string()
          .trim()
          .min(1, t("onboarding_billing_required_field"))
          .refine((value) => validateChileanPhone(value), {
            message: t("onboarding_billing_phone_invalid"),
          }),
        email: z
          .string()
          .trim()
          .min(1, t("onboarding_billing_required_field"))
          .email({ message: t("invalid_email") }),
      }),
    [t]
  );

  const form = useForm<BillingProfileFormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      businessName: defaultValues?.businessName ?? "",
      rut: defaultValues?.rut ?? "",
      address: defaultValues?.address ?? "",
      businessActivity: defaultValues?.businessActivity ?? "",
      contactName: defaultValues?.contactName ?? "",
      phone: defaultValues?.phone ?? "",
      email: defaultValues?.email ?? "",
    },
  });

  const { isValid } = form.formState;

  useEffect(() => {
    if (onValidChange) {
      onValidChange(isValid);
    }
  }, [isValid, onValidChange]);

  return (
    <FormProvider {...form}>
      <form id={formId} onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col gap-4 px-1">
        <TextField label={t("onboarding_billing_business_name")} {...form.register("businessName")} />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-4">
            <TextField
              label={t("onboarding_billing_rut")}
              placeholder="12.345.678-9"
              {...form.register("rut")}
            />
            <TextField label={t("onboarding_billing_address")} {...form.register("address")} />
            <TextField
              label={t("onboarding_billing_business_activity")}
              {...form.register("businessActivity")}
            />
          </div>

          <div className="flex flex-col gap-4">
            <TextField label={t("onboarding_billing_contact_name")} {...form.register("contactName")} />
            <TextField
              label={t("onboarding_billing_phone")}
              placeholder="+56912345678"
              {...form.register("phone")}
            />
            <TextField type="email" label={t("onboarding_billing_email")} {...form.register("email")} />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
